import { type DashboardWidget } from '@adtrackify/at-tracking-event-types';
import { type Layout } from 'react-grid-layout';

import { BREAKPOINT_TIER } from '@/features/dashboard/types/grid-enums';
import { COLS_BY_TIER } from '@/features/dashboard/constants/grid-constants';
import { getDimensionsForDensity } from './widget-sizes';
import type { WidgetDimensions } from '../types/overview-types';
import { WIDGET_CONSTRAINTS } from '../constants/widgets-constants';
import { DASHBOARD_DENSITY } from '@/features/dashboard/types/dashboard-density-enums';

import { sortWidgetsByCanonicalOrder } from './widget-ordering';

interface Placement {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function packOrderPreservingLayout(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): Layout[] {
  if (!widgets || widgets.length === 0) return [];

  const gridCols = COLS_BY_TIER[tier];
  const orderedWidgets = sortWidgetsByCanonicalOrder(widgets);
  const placements = new Map<string, Placement>();

  let widgetIndex = 0;
  let rowY = 0;

  while (widgetIndex < orderedWidgets.length) {
    const firstWidget = orderedWidgets[widgetIndex];
    const firstDims = clampDims(resolveWidgetDimensions(firstWidget, density, tier), gridCols);

    if (firstDims.w === gridCols) {
      placements.set(firstWidget.id, { x: 0, y: rowY, w: firstDims.w, h: firstDims.h });
      rowY += firstDims.h;
      widgetIndex += 1;
      continue;
    }

    const slotWidth = firstDims.w;
    const slotCount = Math.max(1, Math.floor(gridCols / slotWidth));
    const rowHeight = getRowHeight({
      widgets: orderedWidgets,
      startIndex: widgetIndex,
      density,
      tier,
      gridCols,
      slotCount,
    });
    const nextIndex = packStackedRow({
      widgets: orderedWidgets,
      startIndex: widgetIndex,
      density,
      tier,
      gridCols,
      rowY,
      rowHeight,
      slotWidth,
      slotCount,
      placements,
    });

    if (nextIndex === widgetIndex) {
      placements.set(firstWidget.id, { x: 0, y: rowY, w: firstDims.w, h: firstDims.h });
      widgetIndex += 1;
    } else {
      widgetIndex = nextIndex;
    }

    rowY += rowHeight;
  }

  return orderedWidgets.map(widget => buildLayoutItem(widget, placements.get(widget.id), gridCols));
}

interface PackStackedRowArgs {
  widgets: DashboardWidget[];
  startIndex: number;
  density: DASHBOARD_DENSITY;
  tier: BREAKPOINT_TIER;
  gridCols: number;
  rowY: number;
  rowHeight: number;
  slotWidth: number;
  slotCount: number;
  placements: Map<string, Placement>;
}

function packStackedRow({
  widgets,
  startIndex,
  density,
  tier,
  gridCols,
  rowY,
  rowHeight,
  slotWidth,
  slotCount,
  placements,
}: PackStackedRowArgs): number {
  let widgetIndex = startIndex;
  const slotUsedHeights = Array.from({ length: slotCount }, () => 0);

  for (let yOffset = 0; yOffset < rowHeight && widgetIndex < widgets.length; yOffset += 1) {
    for (let slotIndex = 0; slotIndex < slotCount && widgetIndex < widgets.length; slotIndex += 1) {
      if (slotUsedHeights[slotIndex] > yOffset) continue;
      if (slotUsedHeights[slotIndex] < yOffset) continue;

      const widget = widgets[widgetIndex];
      const dims = clampDims(resolveWidgetDimensions(widget, density, tier), gridCols);

      if (dims.w === gridCols) return widgetIndex;
      if (yOffset + dims.h > rowHeight) return widgetIndex;

      placements.set(widget.id, {
        x: slotIndex * slotWidth,
        y: rowY + yOffset,
        w: slotWidth,
        h: dims.h,
      });
      slotUsedHeights[slotIndex] += dims.h;
      widgetIndex += 1;
    }
  }

  return widgetIndex;
}

interface GetRowHeightArgs {
  widgets: DashboardWidget[];
  startIndex: number;
  density: DASHBOARD_DENSITY;
  tier: BREAKPOINT_TIER;
  gridCols: number;
  slotCount: number;
}

function getRowHeight({
  widgets,
  startIndex,
  density,
  tier,
  gridCols,
  slotCount,
}: GetRowHeightArgs): number {
  let rowHeight = 1;
  let inspectedSlots = 0;

  for (
    let index = startIndex;
    index < widgets.length && inspectedSlots < slotCount;
    index += 1
  ) {
    const dims = clampDims(resolveWidgetDimensions(widgets[index], density, tier), gridCols);
    if (dims.w === gridCols) break;

    rowHeight = Math.max(rowHeight, dims.h);
    inspectedSlots += 1;
  }

  return rowHeight;
}

function resolveWidgetDimensions(
  widget: DashboardWidget,
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): WidgetDimensions {
  return getDimensionsForDensity(widget.chartType, density, tier);
}

function clampDims(dims: WidgetDimensions, gridCols: number): WidgetDimensions {
  return {
    w: Math.max(1, Math.min(dims.w, gridCols)),
    h: Math.max(1, dims.h),
  };
}

function buildLayoutItem(
  widget: DashboardWidget,
  placement: Placement | undefined,
  gridCols: number
): Layout {
  const constraints = WIDGET_CONSTRAINTS[widget.chartType ?? 'area'];
  const w = placement?.w ?? Math.min(constraints?.w ?? 3, gridCols);
  const h = placement?.h ?? constraints?.h ?? 1;

  return {
    i: widget.id,
    x: placement?.x ?? 0,
    y: placement?.y ?? 0,
    w,
    h,
    minW: constraints?.minW ?? 2,
    maxW: Math.min(constraints?.maxW ?? gridCols, gridCols),
    minH: constraints?.minH ?? 1,
    maxH: Math.max(h, constraints?.maxH ?? 6),
  };
}
